import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'

import { Store } from '@ngrx/store'
import { isEqual as _isEqual } from 'lodash/fp'
import { DrawerModule } from 'primeng/drawer'
import { SliderModule } from 'primeng/slider'
import { TooltipModule } from 'primeng/tooltip'

import { distinctUntilChanged, from, map, withLatestFrom } from 'rxjs'
import { filter } from 'rxjs/operators'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { AiBuilderPlanViewComponent } from '@flowx/designer/modules/ai-builder/components/ai-builder-plan-view/ai-builder-plan-view.component'
import { AiBuilderRailComponent } from '@flowx/designer/modules/ai-builder/components/ai-builder-rail/ai-builder-rail.component'
import {
  AI_BUILDER_RAIL_ANIMATION_MS,
  AiBuilderStore,
} from '@flowx/designer/modules/ai-builder/store/ai-builder.store'
import { AiWindowComponent } from '@flowx/designer/modules/new-ai'
import { AiCoordinatorActions } from '@flowx/designer/modules/new-ai/agents/coordinator/store/coordinator.actions'
import { AgentDocumentsComponent } from '@flowx/designer/modules/new-ai/modules/command-center/components/agent-documents/agent-documents.component'
import { AgentFooterComponent } from '@flowx/designer/modules/new-ai/modules/command-center/components/agent-footer/agent-footer.component'
import { AgentHeaderComponent } from '@flowx/designer/modules/new-ai/modules/command-center/components/agent-header/agent-header.component'
import { AgentPlanComponent } from '@flowx/designer/modules/new-ai/modules/command-center/components/agent-plan/agent-plan.component'
import { GeneratedPlansComponent } from '@flowx/designer/modules/new-ai/modules/command-center/components/generated-plans/generated-plans.component'
import { SideChatWindowComponent } from '@flowx/designer/modules/new-ai/modules/command-center/components/side-chat/side-chat.component'
import { AiFabComponent } from '@flowx/designer/modules/new-ai/modules/window/components/fab/fab.component'
import {
  AiActions,
  AiPermissionActions,
  KeyboardActions,
} from '@flowx/designer/modules/new-ai/store/ai.actions'
import { AiSelectors } from '@flowx/designer/modules/new-ai/store/ai.selectors'
import { FunctionNavigationService } from '@flowx/designer/modules/script-builder/components/code-editor/function-navigation.service'
import { KeyboardService } from '@flowx/designer/shared/services/keyboard.service'

import { LayoutThemeService } from './modules/layout/services/layout-theme.service'
import { MonacoLoaderService } from './modules/monaco-code-editor/monaco-loader.service'
import { ApplicationPermissionsStore } from './modules/projects/store/application-permissions.store'
import { ApplicationDetailsStore } from './modules/projects/store/application-version-details.store'
import { AppEffectsService } from './shared/effector/app/app.effects'
import { NavigationErrorHandlerService } from './shared/services/navigation-error-handler.service'
import { UrlParametersStoreService } from './shared/services/url-parameters-store.service'
import { selectAllRouteParams } from './shared/store/selectors/router.selectors'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: { class: 'block h-full w-full' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    SliderModule,
    DrawerModule,
    TooltipModule,
    AiFabComponent,
    AgentPlanComponent,
    AgentHeaderComponent,
    AgentFooterComponent,
    SideChatWindowComponent,
    AgentDocumentsComponent,
    GeneratedPlansComponent,
    AiBuilderRailComponent,
    AiBuilderPlanViewComponent,
  ],
})
export class AppComponent {
  #appEffectsService = inject(AppEffectsService)
  #monacoLoaderService = inject(MonacoLoaderService)
  #functionNavigationService = inject(FunctionNavigationService)
  // Construct the theme service at app startup so its constructor effect
  // applies `.flowx-dark` to <html> on every route (login, workspace-select),
  // not just inside main-layout where the toggle lives.
  #layoutThemeService = inject(LayoutThemeService)
  #urlParametersStoreService = inject(UrlParametersStoreService)
  #store = inject(Store)
  #keyboardService = inject(KeyboardService)
  #applicationPermissionsStore = inject(ApplicationPermissionsStore)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #navigationErrorHandlerService = inject(NavigationErrorHandlerService)
  #modalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)

  #aiFabInstance: any = null

  /** The AI-assisted builder (Make / Plan in the header), toggled by ⌘K. */
  aiBuilder = inject(AiBuilderStore)

  /** The rail's travel, stamped onto the shell as `--ai-rail-animation`; the store holds the rail
   * mounted for exactly this long while it leaves. */
  readonly railAnimationMs = AI_BUILDER_RAIL_ANIMATION_MS

  showAiFab = this.#store.selectSignal(AiSelectors.isAiFabVisible)
  showPendingMessage = computed(() => this.#store.selectSignal(AiSelectors.pendingAiResponse)())
  hasMessages = this.#store.selectSignal(AiSelectors.hasMessages)
  applicationName = computed(
    () => this.#applicationDetailsStore.applicationVersion()?.appName ?? null
  )

  zenModeActivated = false

  documentsSidebarVisible = false
  archivedSidebarVisible = false
  planSidebarVisible = false
  chatSidebarVisible = false
  planEnabled = false
  autonomyLevel = Number(localStorage.getItem('autonomyLevel')) || 0

  constructor() {
    effect(() => {
      // The store now exposes one designer-AI flag; the new-ai store still wants per-agent rights.
      const hasAiAccess = this.#applicationPermissionsStore.designerAiAccess()
      const aiAccessRights = {
        showAssistantAgent: hasAiAccess,
        showAnalystAgent: hasAiAccess,
        showDesignerAgent: hasAiAccess,
        showDeveloperAgent: hasAiAccess,
        canViewDocuments: hasAiAccess,
        canUploadDocuments: hasAiAccess,
      }
      this.#store.dispatch(
        AiPermissionActions.loadAiPermissionsSuccess({ permissions: aiAccessRights })
      )
    })

    this.onAutonomyLevelChange({ value: this.autonomyLevel })

    effect(() => {
      const showFloatingWindow = this.#store.selectSignal(AiSelectors.isExpanded)()
      const zenMode = this.#store.selectSignal(AiSelectors.isZenMode)()

      this.zenModeActivated = zenMode

      this.#store
        .select(selectAllRouteParams)
        .pipe(
          map((urlParams) => ({
            appId: urlParams.appId,
            appVersionId: urlParams.appVersionId,
            processId: urlParams.processId,
          })),
          distinctUntilChanged(_isEqual),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(({ appId, appVersionId }) => {
          this.#store.dispatch(
            AiActions.setAppIdAndVersionId({ appId, appName: this.applicationName(), appVersionId })
          )
        })

      if (zenMode) {
        if (!this.hasMessages()) {
          this.#store.dispatch(AiCoordinatorActions.introMessage())
        }
      } else {
        this.#store.dispatch(AiActions.closeChatConfirmed())
      }

      if (showFloatingWindow && !this.#aiFabInstance) {
        this.#aiFabInstance = this.#modalService.show(AiWindowComponent, {
          backdropClass: 'hidden',
          ignoreProgrammaticClose: true,
          hostClass: 'z-[1005]',
        })
      } else if (!showFloatingWindow) {
        this.#aiFabInstance?.close()
        this.#aiFabInstance = null
      }

      this.planEnabled = this.#store.selectSignal(AiSelectors.isPlanEnabled)()
      this.planSidebarVisible = this.#store.selectSignal(AiSelectors.isPlanSidebarVisible)()
      this.chatSidebarVisible = this.#store.selectSignal(AiSelectors.isChatSidebarVisible)()
      this.documentsSidebarVisible = this.#store.selectSignal(
        AiSelectors.isDocumentsSidebarVisible
      )()
      this.archivedSidebarVisible = this.#store.selectSignal(AiSelectors.isArchivedSidebarVisible)()
      this.autonomyLevel = this.#store.selectSignal(AiSelectors.autonomyLevel)()
    })

    // Cmd+K → toggle the AI builder (Make / Plan).
    this.#keyboardService.metaK
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.aiBuilder.toggleList() || this.aiBuilder.toggle())

    // Cmd+Option+K → toggle zen mode (full AI Command Center).
    // The `isAiFabVisible` filter was dropped 2026-06-18 — it gated on
    // permissions + active-agent state, which left users on
    // non-designer pages stranded with a silent shortcut. Zen mode is
    // the user's primary entry point; if AI isn't enabled for the page
    // they'll see the empty state inside the panel, which is more
    // discoverable than a no-op keystroke.
    this.#keyboardService.metaAltK
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.#store.dispatch(KeyboardActions.toggleZenMode()))

    // Cmd+J → open small floating command palette
    this.#keyboardService.metaJ
      .pipe(
        withLatestFrom(this.#store.select(AiSelectors.isAiFabVisible)),
        filter(([, isAiFabVisible]) => isAiFabVisible),
        takeUntilDestroyed()
      )
      .subscribe(() => this.#store.dispatch(KeyboardActions.searchShortcut()))

    this.#keyboardService.escape
      .pipe(
        withLatestFrom(
          this.#store.select(AiSelectors.isAiFabVisible),
          this.#store.select(AiSelectors.isBreadcrumbsBackDisabled)
        ),
        filter(([, isAiFabVisible]) => isAiFabVisible),
        takeUntilDestroyed()
      )
      .subscribe(([, , isBreadcrumbsBackDisabled]) =>
        this.#store.dispatch(KeyboardActions.escape({ isBreadcrumbsBackDisabled }))
      )

    this.#urlParametersStoreService.init()
    // One global set of function-navigation providers serves every script
    // editor, Angular and React alike — they share the same window.monaco.
    from(this.#monacoLoaderService.load())
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: () => this.#functionNavigationService.register(),
        error: (error) =>
          console.error('Monaco failed to load; function navigation is unavailable', error),
      })
    this.#navigationErrorHandlerService.init()
  }

  hideDocumentsSidebar(): void {
    this.#store.dispatch(AiActions.setDocumentsSidebarVisible({ isDocumentsSidebarVisible: false }))
  }

  hideArchivedSidebar(): void {
    this.#store.dispatch(AiActions.setArchivedSidebarVisible({ isArchivedSidebarVisible: false }))
  }

  hidePlanSidebar(): void {
    this.#store.dispatch(AiActions.setPlanSidebarVisible({ isPlanSidebarVisible: false }))
  }

  hideChatSidebar(): void {
    this.#store.dispatch(AiActions.setChatSidebarVisible({ isChatSidebarVisible: false }))
  }

  routerOutletWrapperClick(): void {
    if (this.#store.selectSignal(AiSelectors.isExpanded)()) {
      this.#store.dispatch(AiActions.outsideOfAiChatClick())
    }
  }

  onAutonomyLevelChange(event: any): void {
    this.#store.dispatch(AiActions.setAutonomyLevel({ autonomyLevel: event.value }))
    localStorage.setItem('autonomyLevel', String(event.value))
  }
}
